// Produto ativo do user (PO1-2060 + PO1-2157).
//
// Precedência de resolução:
//   1. localStorage.produtoAtual  — "intenção atual" do user neste device
//      (gravado pelo AppSwitcher; sobrevive entre navegações).
//   2. user.produtoPadrao         — preferência do user (DB, cross-device).
//   3. PRODUCT_ORDER[0] contratado — fallback determinístico (Flow primeiro).
//
// Notas:
// - Em telas compartilhadas (Transportadoras, Acessos etc), o produto ativo
//   continua sendo o último que o user escolheu — o item aparece no menu de
//   todos os produtos que o contêm.
// - Setor 'admin' vê EVERLOG_ADMIN como mais um produto no switcher.
// - Setor 'transportadora' não usa esse conceito (Sidebar dele tem caminho
//   próprio sem produto-first).

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useFeatures } from "./features";
import { useAuth } from "./auth";
import { PRODUCT_ORDER } from "../routes/products";
// PO1-3508: leitura/gravação do storage num módulo sem imports do Hub, para o
// hook do chat do Autopilot ler o produto fora do provider e do Router.
import { gravarProdutoAtual, lerProdutoAtual } from "./produtoAtualStorage";

const ActiveProductContext = createContext({});

const readStored = lerProdutoAtual;
const writeStored = gravarProdutoAtual;
export { lerProdutoAtual };

export const ActiveProductProvider = ({ children }) => {
  const { codes, loading } = useFeatures();
  const { user } = useAuth();
  const [activeProduct, setActiveProductState] = useState(() => readStored());

  // Quando features carregam, valida o produto guardado.
  // Se o guardado não está mais contratado (ou nunca esteve), consulta:
  //   1) preferência do user (produtoPadrao, vem do /users/me)
  //   2) primeiro produto contratado em PRODUCT_ORDER
  useEffect(() => {
    if (loading) return;
    if (!codes || codes.size === 0) return;

    if (activeProduct && codes.has(activeProduct)) return;

    const preferido = user && user.produtoPadrao;
    const fallback =
      (preferido && codes.has(preferido) && preferido) ||
      PRODUCT_ORDER.find((c) => codes.has(c)) ||
      null;
    if (fallback !== activeProduct) {
      setActiveProductState(fallback);
      writeStored(fallback);
    }
  }, [codes, loading, activeProduct, user]);

  const setActiveProduct = useCallback((code) => {
    setActiveProductState(code);
    writeStored(code);
  }, []);

  const value = useMemo(
    () => ({ activeProduct, setActiveProduct }),
    [activeProduct, setActiveProduct],
  );

  return (
    <ActiveProductContext.Provider value={value}>
      {children}
    </ActiveProductContext.Provider>
  );
};

export const useActiveProduct = () => {
  const context = useContext(ActiveProductContext);
  if (!context) {
    throw new Error(
      "useActiveProduct must be used within an ActiveProductProvider",
    );
  }
  return context;
};
